import React, { useEffect, useState, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import billingService from '../services/billingService';

function getRoleFromToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try { return jwtDecode(token).role; } catch { return null; }
}

const BLOCKED_CONTENT = {
  trial_expired: {
    icon: '⏰',
    title: 'Your free trial has ended',
    body: 'Your 14-day free trial has expired. Contact your administrator to subscribe and restore access.',
  },
  past_due: {
    icon: '💳',
    title: 'Payment required',
    body: 'Your subscription payment is past due. Please update your payment method to restore access.',
  },
  canceled: {
    icon: '🚫',
    title: 'Subscription canceled',
    body: 'Your quickLOG subscription has been canceled. Contact your administrator to reactivate.',
  },
  inactive: {
    icon: '🔒',
    title: 'Account deactivated',
    body: 'This account has been deactivated. Contact support for assistance.',
  },
};

export default function SubscriptionGate({ children }) {
  const [status, setStatus]       = useState(null); // null = loading
  const [trialEndsAt, setTrialEndsAt] = useState(null);

  const check = useCallback(() => {
    const role = getRoleFromToken();
    if (!role) { setStatus('ok'); return; }
    if (role === 'superadmin') { setStatus('ok'); return; }

    billingService.getSubscriptionStatus()
      .then(data => {
        setTrialEndsAt(data.trialEndsAt || null);
        const s = data.status;
        setStatus((s === 'active' || s === 'trialing') ? 'ok' : (s || 'ok'));
      })
      .catch(() => setStatus('ok')); // fail open — never block on network error
  }, []);

  useEffect(() => {
    check();
    const onAuth = () => check();
    window.addEventListener('authChanged', onAuth);
    return () => window.removeEventListener('authChanged', onAuth);
  }, [check]);

  // Still loading — render nothing to avoid flash of blocked screen on fast networks
  if (status === null) return null;

  const blocked = status !== 'ok';
  const content = BLOCKED_CONTENT[status];

  // Trial countdown banner (shown when active but trial hasn't expired yet)
  const trialBanner = !blocked && trialEndsAt && (() => {
    const daysLeft = Math.ceil((new Date(trialEndsAt) - Date.now()) / 86400000);
    if (daysLeft > 14 || daysLeft < 0) return null;
    const urgent = daysLeft <= 3;
    return (
      <div style={{
        background: urgent ? '#dc3545' : '#fff3cd',
        color: urgent ? '#fff' : '#664d03',
        padding: '9px 24px',
        fontSize: 13,
        textAlign: 'center',
        fontWeight: 500,
        borderBottom: urgent ? 'none' : '1px solid #ffecb5',
        zIndex: 1050,
        position: 'relative',
      }}>
        {daysLeft === 0
          ? 'Your free trial expires today.'
          : `Free trial: ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining.`}
        {' '}Contact your administrator to subscribe.
      </div>
    );
  })();

  if (blocked && content) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0f3460 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99999,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      }}>
        <div style={{
          background: '#fff', borderRadius: 20,
          padding: '52px 44px', maxWidth: 480, width: '90%',
          textAlign: 'center',
          boxShadow: '0 32px 80px rgba(0,0,0,0.45)',
        }}>
          <div style={{ fontSize: 60, marginBottom: 20, lineHeight: 1 }}>{content.icon}</div>
          <div style={{
            display: 'inline-block', background: '#f8f9fa', borderRadius: 6,
            padding: '3px 12px', fontSize: 11, fontWeight: 700, letterSpacing: 1,
            textTransform: 'uppercase', color: '#666', marginBottom: 16,
          }}>
            quickLOG
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 12px', color: '#1a1a2e' }}>
            {content.title}
          </h2>
          <p style={{ color: '#555', fontSize: 15, lineHeight: 1.65, margin: '0 0 36px' }}>
            {content.body}
          </p>
          <div style={{ fontSize: 12, color: '#bbb' }}>
            Need help?{' '}
            <a href="mailto:support@quicklog.io" style={{ color: '#4f79e8', textDecoration: 'none' }}>
              support@quicklog.io
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{trialBanner}{children}</>;
}
